import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { analytics } from "../lib/analytics";

export interface RankingItem {
  /** The model name (AI mode) or custom text entry (generic mode) */
  model: string | null;
  blurb: string;
}

export type RankingState = [RankingItem, RankingItem, RankingItem];

interface UseRankingOptions {
  initialRankings?: RankingState;
  initialTitle?: string;
  initialHandle?: string;
}

interface UseRankingReturn {
  rankings: RankingState;
  setRankings: (rankings: RankingState) => void;
  setModel: (position: 1 | 2 | 3, model: string | null) => void;
  setBlurb: (position: 1 | 2 | 3, blurb: string) => void;
  title: string;
  setTitle: (title: string) => void;
  handle: string;
  setHandle: (handle: string) => void;
  clearRankings: () => void;
  isComplete: boolean;
  hasAnySelection: boolean;
}

const EMPTY_ITEM: RankingItem = { model: null, blurb: "" };
const EMPTY_RANKINGS: RankingState = [
  { ...EMPTY_ITEM },
  { ...EMPTY_ITEM },
  { ...EMPTY_ITEM },
];

const MAX_BLURB_LENGTH = 60;
const MAX_TITLE_LENGTH = 30;
const MAX_HANDLE_LENGTH = 3;

export function useRanking(options: UseRankingOptions = {}): UseRankingReturn {
  const { initialRankings = EMPTY_RANKINGS, initialTitle = "", initialHandle = "" } = options;

  const [rankings, setRankingsState] = useState<RankingState>(
    () => initialRankings.map((item) => ({ ...item })) as RankingState
  );
  
  const [title, setTitleState] = useState<string>(initialTitle);
  const [handle, setHandleState] = useState<string>(initialHandle);

  const setRankings = useCallback((newRankings: RankingState) => {
    setRankingsState(newRankings);
  }, []);

  const setModel = useCallback((position: 1 | 2 | 3, model: string | null) => {
    setRankingsState((prev) => {
      const index = position - 1;
      return prev.map((item, i) =>
        i === index ? { ...item, model } : { ...item }
      ) as RankingState;
    });
  }, []);

  const setBlurb = useCallback((position: 1 | 2 | 3, blurb: string) => {
    setRankingsState((prev) => {
      const index = position - 1;
      return prev.map((item, i) =>
        i === index ? { ...item, blurb: blurb.slice(0, MAX_BLURB_LENGTH) } : { ...item }
      ) as RankingState;
    });
  }, []);

  const setTitle = useCallback((newTitle: string) => {
    setTitleState(newTitle.slice(0, MAX_TITLE_LENGTH));
  }, []);

  const setHandle = useCallback((newHandle: string) => {
    setHandleState(newHandle.slice(0, MAX_HANDLE_LENGTH));
  }, []);

  // Track completion events (fire only once per session)
  const hasFiredRankingCompleted = useRef(false);
  const hasFiredRankingAndBlurbsCompleted = useRef(false);

  const clearRankings = useCallback(() => {
    setRankingsState([
      { ...EMPTY_ITEM },
      { ...EMPTY_ITEM },
      { ...EMPTY_ITEM },
    ]);
    setTitleState("");
    setHandleState("");
    // Reset completion tracking so events can fire again
    hasFiredRankingCompleted.current = false;
    hasFiredRankingAndBlurbsCompleted.current = false;
  }, []);

  const isComplete = useMemo(
    () => rankings.every((item) => item.model !== null),
    [rankings]
  );

  const hasAllBlurbs = useMemo(
    () => rankings.every((item) => item.blurb.trim() !== ""),
    [rankings]
  );

  const hasAnySelection = useMemo(
    () => rankings.some((item) => item.model !== null),
    [rankings]
  );

  useEffect(() => {
    if (isComplete && !hasFiredRankingCompleted.current) {
      hasFiredRankingCompleted.current = true;
      const models = rankings.map((r) => r.model!) as [string, string, string];
      analytics.rankingCompleted(models);
    }
  }, [isComplete, rankings]);

  useEffect(() => {
    if (isComplete && hasAllBlurbs && !hasFiredRankingAndBlurbsCompleted.current) {
      hasFiredRankingAndBlurbsCompleted.current = true;
      const models = rankings.map((r) => r.model!) as [string, string, string];
      analytics.rankingAndBlurbsCompleted(models);
    }
  }, [isComplete, hasAllBlurbs, rankings]);

  return {
    rankings,
    setRankings,
    setModel,
    setBlurb,
    title,
    setTitle,
    handle,
    setHandle,
    clearRankings,
    isComplete,
    hasAnySelection,
  };
}

